import { atom, WritableAtom, useSetAtom, PrimitiveAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect, useRef } from "react";
import { api } from "~/api";
import { useIsAwsSettingPage } from "./envUtils";

// 動的にcustomerIdを取得する関数（外部から注入）
let getCustomerIdFn: (() => Promise<string>) | null = null;

// customerIdを動的に取得する関数を設定
export const setCustomerIdProvider = (fn: () => Promise<string>) => {
  getCustomerIdFn = fn;
};

// 実際のcustomerIdを取得（ローカルストレージ優先、なければCognitoから取得）
const getActualCustomerId = async (fallbackCustomerId: string): Promise<string> => {
  // まずローカルストレージをチェック
  const storedCustomerId = localStorage.getItem("customerId");
  if (storedCustomerId && storedCustomerId !== "null") {
    return JSON.parse(storedCustomerId);
  }

  // ローカルストレージにない場合はCognitoから取得
  if (getCustomerIdFn) {
    try {
      const customerId = await getCustomerIdFn();
      // 取得したcustomerIdをローカルストレージに保存
      localStorage.setItem("customerId", JSON.stringify(customerId));
      return customerId;
    } catch (error) {
      console.error("Failed to get dynamic customer ID, using fallback:", error);
      // Cognitoエラーの場合、fallbackが空文字なら認証エラーを投げる
      if (!fallbackCustomerId || fallbackCustomerId.trim() === "") {
        throw new Error("customerId is required for AWS environment. Please authenticate first.");
      }
      return fallbackCustomerId;
    }
  }

  // Cognito関数が設定されていない場合、fallbackが空文字なら認証エラーを投げる
  if (!fallbackCustomerId || fallbackCustomerId.trim() === "") {
    throw new Error("customerId is required for AWS environment. Please authenticate first.");
  }
  return fallbackCustomerId;
};

// customerIdがatomの場合とstringの場合を処理するヘルパー関数
const getCustomerIdValue = async (
  customerId: string | PrimitiveAtom<string | null>,
  getAtomValue?: (atom: PrimitiveAtom<string | null>) => string | null
): Promise<string> => {
  if (typeof customerId === "string") {
    return await getActualCustomerId(customerId);
  } else {
    // atomの場合
    const atomValue = getAtomValue?.(customerId);
    if (atomValue) {
      return atomValue;
    }
    // atomに値がない場合は認証が必要であることを示すエラーを投げる
    try {
      return await getActualCustomerId("");
    } catch (error) {
      // 認証が必要な場合のエラーメッセージを明確にする
      console.error("Customer ID authentication failed:", error);
      throw new Error("Authentication required. Please sign in to access AWS DynamoDB features.");
    }
  }
};

/**
 * Atomの保存先設定
 */

// DynamoDB設定を持つAtom型
type AtomWithDynamoDBConfig<T> = {
  localStorageKey: string;
  tableName: string;
  customerId: string | PrimitiveAtom<string | null>;
  fromDynamoDB: (item: Record<string, unknown>) => T;
  toDynamoDB: (value: T) => Record<string, unknown>;
  valueAtom: PrimitiveAtom<T | null>;
};

type AtomWithDynamoDBMetadata<T> = WritableAtom<T, [T], void | Promise<void>> & {
  __dynamoDBConfig?: AtomWithDynamoDBConfig<T>;
};

/**
 * DynamoDBのみに保存するAtom（共有設定用）
 *
 * オンプレ環境:
 *   - atomWithStorageを使用（localStorage）
 *
 * AWS環境:
 *   読み取り:
 *   - localStorageにキャッシュがあればそれを使用（DynamoDB取得スキップ）
 *   - キャッシュがなければDynamoDBから取得
 *   - 値の優先順位:
 *     1. localStorageの値
 *     2. DynamoDBから取得した値（メモリ上のみ）
 *     3. defaultValue
 *   書き込み:
 *     - Web管理画面からの操作時:
 *       - DynamoDBのみに保存
 *       - localStorageには保存しない
 *       - メモリ上のatomも更新
 *
 *     - チャット画面からの操作時:
 *       - localStorageに保存
 *       - DynamoDBには保存しない
 *       - メモリ上のatomも更新
 */
export const atomWithDynamoDB = <T>(
  localStorageKey: string,
  tableName: string,
  customerId: string | PrimitiveAtom<string | null>,
  defaultValue: T,
  options: {
    // DynamoDBのレスポンスをT型に変換する関数
    fromDynamoDB: (item: Record<string, unknown>) => T;
    // T型をDynamoDBの形式に変換する関数
    toDynamoDB: (value: T) => Record<string, unknown>;
  }
): AtomWithDynamoDBMetadata<T> => {
  // AWS環境かどうかを判定
  const isAWS = process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws";

  if (!isAWS) {
    // オンプレ環境: 通常のatomWithStorage;
    return atomWithStorage<T>(localStorageKey, defaultValue) as AtomWithDynamoDBMetadata<T>;
  }

  // AWS環境:
  // DynamoDBから取得した値を保持するatom（メモリ上のみ）
  const valueAtom = atom<T | null>(null);

  // 読み取り・書き込みを制御するAtom
  const combinedAtom = atom(
    (get) => {
      // DynamoDB値優先、なければデフォルト値
      const dynamoValue = get(valueAtom);
      return dynamoValue !== null ? dynamoValue : defaultValue;
    },
    async (get, set, newValue: T) => {
      // URLパスに応じて保存先を分岐
      const currentPath = window.location.hash;
      const isSettingPage = currentPath.startsWith("#/setting");

      if (isSettingPage) {
        // #/setting: DynamoDBに保存
        const updateData = options.toDynamoDB(newValue);

        try {
          // customerIdの値を取得（stringまたはatomから）
          const actualCustomerId = await getCustomerIdValue(customerId, (atom) => get(atom));

          // serverのUpdateItem APIを呼び出し
          await api.dynamodb.updateItem.mutate({
            tableName,
            key: { customerId: actualCustomerId },
            updateData: {
              ...updateData,
              updatedAt: new Date().toISOString(),
              updatedBy: actualCustomerId,
            },
          });
          // DynamoDB保存成功後、メモリ上のatomを更新（UI即反映）
          set(valueAtom, newValue);
        } catch (error) {
          // 認証エラーかどうかを判定
          if (error instanceof Error && error.message.includes("Authentication required")) {
            console.info("DynamoDB操作には認証が必要です:", error.message);
            // 認証エラーの場合はlocalStorageにフォールバック
            localStorage.setItem(localStorageKey, JSON.stringify(newValue));
            set(valueAtom, newValue);
          } else {
            console.error("DynamoDBへの保存に失敗しました:", error);
          }
        }
      } else {
        // #/chat: localStorageに保存
        if (Array.isArray(newValue) && newValue.length === 0) {
          // 空配列の場合はローカルストレージに保存しない
          localStorage.removeItem(localStorageKey);
          set(valueAtom, newValue);
        } else {
          localStorage.setItem(localStorageKey, JSON.stringify(newValue));
          // メモリ上のatomも更新
          set(valueAtom, newValue);
        }
      }
    }
  ) as AtomWithDynamoDBMetadata<T>;

  // DynamoDB設定を保持（フック用）
  combinedAtom.__dynamoDBConfig = {
    localStorageKey,
    tableName,
    customerId,
    fromDynamoDB: options.fromDynamoDB,
    toDynamoDB: options.toDynamoDB,
    valueAtom,
  };

  return combinedAtom;
};

/**
 * localStorageから値を読み込んでAtomに設定するヘルパー関数
 * @param localStorageKey - localStorageのキー
 * @param setDynamoDBValue - DynamoDB値を設定する関数
 * @param onComplete - 読み込み完了時のコールバック
 * @returns void
 */
const loadFromLocalStorage = <T>(
  localStorageKey: string,
  setDynamoDBValue: (value: T | null) => void,
  onComplete: () => void
) => {
  try {
    const storedValue = localStorage.getItem(localStorageKey);
    if (storedValue !== null) {
      const parsedValue = JSON.parse(storedValue) as T;
      setDynamoDBValue(parsedValue);
    }
  } catch (error) {
    console.error(`localStorageの${localStorageKey}のパースに失敗しました:`, error);
  } finally {
    onComplete();
  }
};

/**
 * DynamoDBから値を読み込んでAtomに設定するヘルパー関数
 * @param config - DynamoDB設定
 * @param setDynamoDBValue - DynamoDB値を設定する関数
 * @param onComplete - 読み込み完了時のコールバック
 * @returns void
 */
const loadFromDynamoDB = async <T>(
  config: {
    localStorageKey: string;
    tableName: string;
    customerId: string | PrimitiveAtom<string | null>;
    fromDynamoDB: (item: Record<string, unknown>) => T;
  },
  setDynamoDBValue: (value: T | null) => void,
  onComplete: () => void
) => {
  const { localStorageKey, tableName, customerId, fromDynamoDB } = config;
  try {
    const actualCustomerId = await getCustomerIdValue(customerId);
    const response = await api.dynamodb.getItem.query({
      tableName,
      key: { customerId: actualCustomerId },
    });

    if (response.success && "item" in response && response.item) {
      const dbValue = fromDynamoDB(response.item);
      setDynamoDBValue(dbValue);
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Authentication required")) {
      console.info(`DynamoDB取得には認証が必要です (${localStorageKey}):`, error.message);
    } else {
      console.error(`DynamoDBから${localStorageKey}の取得に失敗しました:`, error);
    }
  } finally {
    onComplete();
  }
};

/**
 * DynamoDB対応Atomの初期化フック
 *
 * 動作:
 *   1. 設定画面の場合は常にDynamoDBから再取得
 *   2. 設定画面以外では、localStorageにキャッシュがあればそれを使用（DynamoDB取得スキップ）
 *   3. キャッシュがなければDynamoDBから取得してatomに設定
 *
 * @param atomMetadata - DynamoDB設定を持つAtom
 * @param onInitialized - 初期化完了時のコールバック（オプション）
 */
export const useDynamoDBAtom = <T>(
  atomMetadata: AtomWithDynamoDBMetadata<T>,
  onInitialized?: () => void
) => {
  const config = atomMetadata.__dynamoDBConfig;
  const setDynamoDBValue = useSetAtom(config?.valueAtom ?? atom(null));
  const hasInitialized = useRef(false);
  const isAwsSettingPage = useIsAwsSettingPage();
  const previousIsAwsSettingPage = useRef(isAwsSettingPage);

  useEffect(() => {
    // 設定画面への遷移を検知
    const isTransitionToSettingPage = !previousIsAwsSettingPage.current && isAwsSettingPage;
    previousIsAwsSettingPage.current = isAwsSettingPage;

    // 既に初期化済みで、設定画面でない、かつ設定画面への遷移でない場合はスキップ
    if (hasInitialized.current && !isAwsSettingPage && !isTransitionToSettingPage) {
      return;
    }

    if (!config) {
      // 設定がない場合は即座に完了扱い
      hasInitialized.current = true;
      onInitialized?.();
      return;
    }

    const onComplete = () => {
      hasInitialized.current = true;
      onInitialized?.();
    };

    const { localStorageKey } = config;
    const storedValue = localStorage.getItem(localStorageKey);
    const setValue = setDynamoDBValue as (value: T | null) => void;

    // 設定画面の場合は常にDynamoDBから取得
    if (isAwsSettingPage) {
      loadFromDynamoDB(config, setValue, onComplete);
      return;
    }

    // localStorageに値がある場合（DynamoDB取得をスキップ）
    if (storedValue !== null) {
      loadFromLocalStorage(localStorageKey, setValue, onComplete);
      return;
    }

    // localStorageに値がない場合のみDynamoDBから取得
    loadFromDynamoDB(config, setValue, onComplete);
  }, [isAwsSettingPage]);
};
